/**
 * usePricingRulePerformance — dashboard stats for a coupon/promotion.
 *
 * Calls the tenant-isolated backend endpoint
 * `zaviago_backend.storefront.coupon_performance.get_pricing_rule_performance`,
 * which sums submitted Sales Orders that carried any Coupon Code linked to this
 * Pricing Rule → redemptions, revenue driven, discount given, distinct customers
 * (new vs repeat) and uses-left. Same mode-aware `client.call()` + auth-store
 * tenant pattern as promotionApi.
 */

import { useQuery } from "@tanstack/react-query";
import { getERPClient } from "@/lib/erp/client";
import { useAuthStore } from "@/lib/store/authStore";

export interface CouponTimePoint {
  date: string; // YYYY-MM-DD
  uses: number;
  revenue: number;
}

export interface CouponTrend {
  uses_30d: number;
  uses_prev_30d: number;
  revenue_30d: number;
  revenue_prev_30d: number;
}

export interface CouponTopProduct {
  item_code: string;
  item_name: string;
  qty: number;
  amount: number;
}

export interface CouponTiming {
  dow: number[]; // Mon..Sun, 7 counts
  last_used: string | null;
  peak_date: string | null;
  peak_uses: number;
}

export interface CouponRoi {
  cost: number | null;
  cost_type: "discount" | "perk";
  return_x: number | null;
}

export interface CouponPerformance {
  redemptions: number;
  used: number;
  uses_left: number | null;
  revenue: number;
  discount_given: number;
  customers: {
    total: number;
    new: number;
    repeat: number;
    new_revenue: number;
    new_revenue_pct: number;
  };
  currency: string;
  coupons: number;
  timeseries: CouponTimePoint[];
  trend: CouponTrend;
  top_products: CouponTopProduct[];
  timing: CouponTiming;
  roi: CouponRoi;
}

export function usePricingRulePerformance(pricingRuleName: string, enabled: boolean) {
  const tenant = useAuthStore((s) => s.currentTenant);
  return useQuery<CouponPerformance>({
    queryKey: ["pricing-rule-performance", pricingRuleName],
    enabled: enabled && !!pricingRuleName && !!tenant,
    staleTime: 60_000,
    queryFn: () =>
      getERPClient().call<CouponPerformance>(
        "zaviago_backend.storefront.coupon_performance.get_pricing_rule_performance",
        { tenant, pricing_rule: pricingRuleName },
      ),
  });
}
